/**
 * ClientPicker — the shared searchable client typeahead.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  2026-05-22 (Anil) — owner-feedback bugs 3 + 4.
 * ─────────────────────────────────────────────────────────────────────────
 *  Two surfaces let a team member re-link a project to a different client:
 *    · the project Re-link editor   (RelinkProject.tsx — bug 3)
 *    · Edit Project → Client field  (AllProjects.tsx EditPanel — bug 4)
 *
 *  Bug 3 used a bare <select> that opened the ENTIRE unfiltered client list.
 *  Bug 4 used a free-text input that behaved like "create a new client" and
 *  never updated the displayed client number.
 *
 *  This ONE component fixes both: a type-to-filter field over the live
 *  client directory. The owner types; matching EXISTING clients appear;
 *  picking one fires `onPick` with the chosen client. It NEVER creates a
 *  client — selection is always from the known directory.
 *
 *  Directory source: the SAME two streams the Clients screen reads —
 *  `listenProjects` (folded to client rows) + `listenClients` (the master
 *  records, so a 0-project standalone client is also pickable). No extra
 *  Firestore cost beyond what the Clients screen already pays. The pure
 *  `clientPickerMatches` predicate is unit-tested.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  clientRowsFromProjects,
  listenClients,
  listenProjects,
  matchesClientRow,
  mergeClientRows,
  type ClientRow,
  type LiveClient,
  type LiveProject,
} from '../lib/projectStore';

/* The typeahead filter is the pure `matchesClientRow` predicate from
 * projectStore (case-insensitive substring over name + number, empty = all)
 * — the SAME predicate the Clients list search uses, so the picker and the
 * list never disagree on what "matches". It is unit-tested there. */

const fieldStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

/**
 * The typeahead. `value` is the currently-linked clientId (so the field
 * shows the current client when nothing is being typed). `onPick` fires
 * with the chosen client row when a team member selects one.
 *
 *   <ClientPicker
 *     value={clientId}
 *     onPick={(c) => { setClientId(c.clientId); … }}
 *     ariaLabel="Re-link project to client"
 *   />
 */
export function ClientPicker({
  value,
  onPick,
  ariaLabel = 'Search and select a client',
  placeholder = 'Type a client name or number…',
  directoryOverride,
}: {
  value: string;
  onPick: (client: ClientRow) => void;
  ariaLabel?: string;
  placeholder?: string;
  /**
   * OPTIONAL pre-built directory. When provided, the picker uses it verbatim
   * and skips its own Firebase subscriptions. Used by the dev render harness
   * (no Firebase) to prove the picker mounts + filters with seeded rows.
   * Production callers omit it — the live `listen*` streams self-populate.
   */
  directoryOverride?: ClientRow[];
}) {
  const injected = directoryOverride != null;
  const [projects, setProjects] = useState<LiveProject[]>([]);
  const [masters, setMasters] = useState<LiveClient[]>([]);
  // `query` is what the owner has typed; null = field shows the current
  // selection (not in "searching" mode).
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Both streams — exactly what the Clients screen subscribes to. Skipped
  // when a directory is injected (the harness path: no Firebase).
  useEffect(() => {
    if (injected) return;
    const unsub = listenProjects(setProjects, {
      /* the picker just shows fewer options if this fails */
    });
    return unsub;
  }, [injected]);
  useEffect(() => {
    if (injected) return;
    const unsub = listenClients(setMasters, {
      /* the picker still works from the project fold if this fails */
    });
    return unsub;
  }, [injected]);

  // The merged client directory — folded project rows + master records, so
  // a brand-new 0-project standalone client is pickable too. An injected
  // directory wins verbatim (harness).
  const directory: ClientRow[] = useMemo(
    () =>
      injected
        ? (directoryOverride as ClientRow[])
        : mergeClientRows(
            clientRowsFromProjects(projects, true),
            masters,
            true,
          ),
    [injected, directoryOverride, projects, masters],
  );

  const current = directory.find((r) => r.clientId === value) || null;

  // What the field shows: the live-typed query while searching, else the
  // current client's label.
  const fieldText =
    query !== null
      ? query
      : current
      ? `${current.name || current.clientId} (${current.clientNo})`
      : '';

  // Filtered matches — only while actively typing.
  const matches = useMemo(() => {
    if (query === null) return [];
    return directory
      .filter((r) => matchesClientRow(r, query))
      .slice(0, 40);
  }, [directory, query]);

  // Close the dropdown on outside-click / Escape (Escape also cancels the
  // in-progress search, restoring the current selection in the field).
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setOpen(false);
        setQuery(null);
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        setOpen(false);
        setQuery(null);
      }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(row: ClientRow) {
    onPick(row);
    setQuery(null);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={fieldText}
        aria-label={ariaLabel}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          // Entering search mode — start from a blank query so the owner
          // can type freely; the current selection is restored on blur.
          setQuery('');
          setOpen(true);
        }}
        style={fieldStyle}
      />
      {open && (
        <div
          role="listbox"
          aria-label="Matching clients"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            maxHeight: 280,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            boxShadow: 'var(--e-2)',
          }}
        >
          {matches.length === 0 ? (
            <div
              style={{
                padding: '10px 12px',
                fontSize: 12.5,
                color: 'var(--ink-mute)',
              }}
            >
              {directory.length === 0
                ? 'Loading clients…'
                : `No existing client matches “${query}”.`}
            </div>
          ) : (
            matches.map((r) => {
              const selected = r.clientId === value;
              return (
                <button
                  key={r.clientId}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => choose(r)}
                  style={{
                    display: 'flex',
                    width: '100%',
                    gap: 10,
                    alignItems: 'baseline',
                    padding: '9px 12px',
                    border: 0,
                    borderBottom: '1px solid var(--rule)',
                    background: selected
                      ? 'var(--accent-wash)'
                      : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: 'var(--ink)',
                    }}
                  >
                    {r.name || r.clientId}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {r.clientNo}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11.5,
                      color: 'var(--ink-mute)',
                    }}
                  >
                    {r.projectCount} project
                    {r.projectCount === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
